'use client'

import { useEffect, useMemo, useState } from 'react'

import _ from 'lodash'
import { MinusCircle, PlusCircle, Loader2, XCircle } from 'lucide-react'

import { useUserRewardRequests } from '../hooks/useRewardRequests'
import {
  hasPendingRewardRequest,
  resolveRewardRequestState
} from '../lib/rewardRequestStatus'

import { RewardRequestBadge } from './RewardRequestBadge'

import { Reward, RewardItem } from '@/features/store/types/reward'
import { useStoreAnalytics } from '@/shared/lib/analytics'
import { api } from '@/shared/services/api'
import { Button } from '@/shared/ui/Button'
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody
} from '@/shared/ui/Modal'


interface RewardModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reward: Reward
}

const MAX_REWARD_REQUESTS_AVAILABILITY_CHECKS = 100

// Status que contam para o limite por usuário (espelha validates_quantity na API).
const COUNTED_STATUSES = ['draft', 'approved', 'delivered'] as const

const DEFAULT_CLAIM_ERROR =
  'Não foi possível concluir o resgate. Tente novamente.'

const extractClaimErrorMessage = (error: unknown): string => {
  const data = _.get(error, 'response.data') as
    | { errors?: unknown; error_message?: unknown }
    | undefined
  const rawErrors = data?.errors
  const isString = (value: unknown): value is string => typeof value === 'string'

  if (Array.isArray(rawErrors)) {
    const messages = rawErrors
      .map(item => (isString(item) ? item : _.get(item, 'message')))
      .filter(isString)
    if (messages.length) return messages.join('. ')
  } else if (rawErrors && typeof rawErrors === 'object') {
    const messages = Object.values(rawErrors as Record<string, unknown>)
      .flat()
      .filter(isString)
    if (messages.length) return messages.join('. ')
  }

  const errorMessage = data?.error_message
  if (isString(errorMessage) && errorMessage) return errorMessage

  return DEFAULT_CLAIM_ERROR
}

export const RewardModal = ({ reward }: RewardModalProps) => {
  const {
    trackStorePurchaseAttempted,
    trackStorePurchaseCompleted,
    trackStorePurchaseFailed
  } = useStoreAnalytics()

  const rewardItems = _.filter(_.get(reward, 'reward_items', []), {
    active: true
  }).map(rewardItem => ({
    ...rewardItem,
    outOfStock: !(rewardItem.quantity - rewardItem.quantity_sold > 0)
  }))

  // O backend filtra por `params[:q]` (ransack) e não expõe reward_id/status
  // como atributos filtráveis aqui; buscamos os resgates do aluno e escopamos
  // client-side para a recompensa aberta.
  const { records: allRequests, refetch: refetchRequests } =
    useUserRewardRequests()

  const requests = useMemo(
    () =>
      allRequests.filter(
        request => request.reward_item?.reward?.id === reward?.id
      ),
    [allRequests, reward?.id]
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentQuantity, setCurrentQuantity] = useState(0)
  const [selectedItemId, setSelectedItemId] = useState<number | undefined>()
  const [feedback, setFeedback] = useState<{
    type: 'error' | 'info'
    text: string
  } | null>(null)

  const selectedItem = _.find(rewardItems, { id: selectedItemId }) as
    | RewardItem
    | undefined

  const minCoin = Math.min(...rewardItems.map(item => item.coin))
  const maxCoin = Math.max(...rewardItems.map(item => item.coin))

  const perUserCap =
    reward?.max_quantity_per_user && reward.max_quantity_per_user > 0
      ? reward.max_quantity_per_user
      : Infinity

  const minQuantity = selectedItem?.min_quantity || 1

  const maxQuantity = Math.min(
    perUserCap,
    selectedItem?.max_quantity || Infinity
  )

  const equalQuantity = selectedItem?.min_quantity === maxQuantity
  const stock = selectedItem
    ? selectedItem.quantity - selectedItem.quantity_sold
    : 0
  const availableQuantity = Math.min(maxQuantity, stock)
  const canPlus = currentQuantity < availableQuantity
  const canMinus = currentQuantity > minQuantity
  const totalCoins = (selectedItem?.coin || 0) * currentQuantity

  const quantityRequested = requests
    .filter(request =>
      (COUNTED_STATUSES as readonly string[]).includes(request.status)
    )
    .reduce((total, request) => request.quantity + total, 0)

  const maxRequestsReached = quantityRequested >= perUserCap

  // Enquanto houver um resgate aguardando aprovação (draft) para esta
  // recompensa, o backend recusa nova solicitação; refletimos isso na UI.
  const hasPending = hasPendingRewardRequest(requests)

  let timer: NodeJS.Timeout

  const handlePlusQuantity = () => {
    if (isSubmitting) return
    setCurrentQuantity(
      Math.min(availableQuantity, Math.max(minQuantity, (currentQuantity || 0) + 1))
    )
  }

  const handleMinusQuantity = () => {
    if (isSubmitting) return
    setCurrentQuantity(
      Math.max(minQuantity, (currentQuantity || 0) - 1)
    )
  }

  const handleClaim = async () => {
    if (isSubmitting || hasPending) return
    setFeedback(null)
    setIsSubmitting(true)

    trackStorePurchaseAttempted(reward, currentQuantity)

    try {
      const data = {
        reward_id: reward?.id,
        reward_item_id: selectedItem?.id,
        quantity: currentQuantity
      }

      let resRewardRequest = await api.post(`/user/reward_requests`, {
        record: data
      })
      let rewardRequest = _.get(resRewardRequest, 'data.record')

      let fetchRewardRequestRetries = 0
      timer = setInterval(async () => {
        try {
          fetchRewardRequestRetries++
          resRewardRequest = await api.get(
            `/user/reward_requests/${rewardRequest.id}`
          )
          rewardRequest = _.get(resRewardRequest, 'data.record')

          if (rewardRequest.status === 'approved') {
            setIsSubmitting(false)
            clearInterval(timer)
            trackStorePurchaseCompleted(reward, currentQuantity)
          } else if (rewardRequest.status === 'insufficient_coins') {
            setIsSubmitting(false)
            clearInterval(timer)
            trackStorePurchaseFailed(
              reward,
              currentQuantity,
              'insufficient_coins'
            )
            setFeedback({
              type: 'error',
              text: 'Você não tem moedas suficientes para resgatar esta recompensa'
            })
          } else if (rewardRequest.status === 'out_of_stock') {
            setIsSubmitting(false)
            clearInterval(timer)
            trackStorePurchaseFailed(reward, currentQuantity, 'out_of_stock')
            setFeedback({
              type: 'error',
              text: 'Este item não tem mais em estoque, tente outro'
            })
          }

          refetchRequests()

          if (
            fetchRewardRequestRetries >= MAX_REWARD_REQUESTS_AVAILABILITY_CHECKS
          ) {
            setIsSubmitting(false)
            clearInterval(timer)
            trackStorePurchaseFailed(reward, currentQuantity, 'unknown')
          }
        } catch (error) {
          setIsSubmitting(false)
          clearInterval(timer)
          trackStorePurchaseFailed(reward, currentQuantity, 'unknown')
        }
      }, 2000)
    } catch (error) {
      setFeedback({ type: 'error', text: extractClaimErrorMessage(error) })
      setIsSubmitting(false)
      trackStorePurchaseFailed(reward, currentQuantity, 'unknown')
    }
  }

  const handleItemClaimAvailable = () =>
    setFeedback({
      type: 'info',
      text: 'Este item da lojinha está disponível para resgate, procure a organização da Missão Futuro para retirar.'
    })

  useEffect(() => {
    return () => {
      clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    if (rewardItems && rewardItems.length && !selectedItem) {
      const inStockRewardItems = rewardItems.filter(
        rewardItem => !rewardItem.outOfStock
      )

      if (inStockRewardItems && inStockRewardItems.length)
        setSelectedItemId(_.get(inStockRewardItems, '0.id'))
    }
  }, [rewardItems])

  useEffect(() => {
    if (selectedItem) setCurrentQuantity(minQuantity)
  }, [selectedItem])

  return (
    <Modal asCard>
      <ModalHeader>
        <ModalTitle>{reward?.name}</ModalTitle>
        <ModalDescription className="hidden">
          {reward?.reward_category?.name}
        </ModalDescription>
      </ModalHeader>
      <ModalBody className="sm:max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="aspect-square">
              <img
                src={reward?.image?.file?.url || '/placeholders/reward.jpg'}
                alt={reward?.name}
                title={reward?.name}
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center mb-2">
              <img src="/icons/coin.svg" alt="coin" className="size-4 mr-2" />
              <span className="font-bold text-lg">
                {minCoin !== maxCoin
                  ? `${minCoin} - ${maxCoin}`
                  : `${minCoin || 'Grátis'}`}
              </span>
            </div>
            {feedback && (
              <div
                className={`mb-4 flex items-start justify-between gap-2 rounded-lg px-4 py-3 text-sm font-medium ${
                  feedback.type === 'error'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-blue-100 text-blue-800'
                }`}
                role="alert"
              >
                <span>{feedback.text}</span>
                <button
                  type="button"
                  aria-label="Fechar"
                  className="shrink-0"
                  onClick={() => setFeedback(null)}
                >
                  <XCircle size={18} />
                </button>
              </div>
            )}
            <div
              dangerouslySetInnerHTML={{ __html: reward?.description || '' }}
              className="mb-6 text-base max-h-40 overflow-y-auto"
            />
            <div className="mt-auto">
              <div className="flex flex-col gap-2 mb-4">
                {requests.map(request => {
                  const state = resolveRewardRequestState(request)
                  if (state === 'none') return null

                  const clickable = state === 'awaiting_pickup'
                  return (
                    <div
                      key={request.id}
                      className={`px-5 py-3 flex bg-gray-100 rounded-lg justify-between items-center gap-2 ${
                        clickable ? 'cursor-pointer' : ''
                      }`}
                      onClick={
                        clickable ? handleItemClaimAvailable : undefined
                      }
                    >
                      <span className="text-gray-700 text-sm font-bold">
                        {request.quantity}x {request.reward_item?.name}
                      </span>
                      <RewardRequestBadge state={state} />
                    </div>
                  )
                })}
              </div>
              {hasPending && (
                <div
                  className="mb-4 rounded-lg bg-amber-100 px-4 py-3 text-sm font-medium text-amber-800"
                  role="status"
                >
                  Você tem um resgate aguardando aprovação. Aguarde a conclusão
                  para solicitar novamente.
                </div>
              )}
              {maxRequestsReached ? (
                <span className="px-4 py-3 text-red-800  bg-red-100 rounded-lg font-bold flex items-center gap-2">
                  <XCircle size={20} className="text-red-400" />
                  Você já solicitou ou resgatou o máximo deste item da lojinha
                  disponível por usuário
                </span>
              ) : (
                <div className="flex flex-col">
                  <span className="mb-2 block">
                    <span className="font-bold">Variação:</span>{' '}
                    {selectedItem?.name}
                  </span>
                  <div className="mb-6 grid grid-cols-3 md:grid-cols-4 gap-4">
                    {rewardItems.map(item => (
                      <div
                        key={`reward-item-${item.id}`}
                        className={`relative ${
                          item.outOfStock ? 'order-2' : 'order-1'
                        }`}
                      >
                        <div
                          className={`aspect-square border-2 ${
                            selectedItemId === item.id
                              ? 'border-primary'
                              : 'border-gray-300'
                          } overflow-hidden rounded-lg ${
                            !item.outOfStock && 'cursor-pointer'
                          }`}
                          onClick={() =>
                            item.outOfStock ? null : setSelectedItemId(item.id)
                          }
                        >
                          <img
                            src={item.image?.file?.url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {item.outOfStock && (
                          <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center rounded-lg">
                            <span className="text-white font-bold text-center">
                              SEM ESTOQUE
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {selectedItem && (
                    <div className="p-2 mt-auto rounded-lg flex flex-col gap-2 bg-gray-100 justify-center">
                      {!equalQuantity && (
                        <div className="flex items-center w-full justify-center">
                          <button
                            type="button"
                            className="bg-transparent p-1 disabled:opacity-50"
                            onClick={handleMinusQuantity}
                            disabled={!canMinus}
                          >
                            <MinusCircle
                              size={30}
                              className={`${
                                canMinus ? 'text-primary' : 'text-gray-400'
                              }`}
                            />
                          </button>
                          <span className="pt-1 mx-1 text-center text-gray-700 font-bold text-lg w-5">
                            {currentQuantity}
                          </span>
                          <button
                            type="button"
                            className="bg-transparent p-1 disabled:opacity-50"
                            onClick={handlePlusQuantity}
                            disabled={!canPlus}
                          >
                            <PlusCircle
                              size={30}
                              className={`${
                                canPlus ? 'text-primary' : 'text-gray-400'
                              }`}
                            />
                          </button>
                        </div>
                      )}
                      <Button
                        variant="primary"
                        type="button"
                        className="flex-grow flex flex-col items-center"
                        disabled={!currentQuantity || isSubmitting || hasPending}
                        loading={isSubmitting}
                        onClick={handleClaim}
                      >
                        {isSubmitting ? (
                          <Loader2
                            size={20}
                            className="animate-spin text-white"
                          />
                        ) : (
                          <>
                            <span className="block">
                              Tentar Resgatar {selectedItem?.name}{' '}
                              {currentQuantity > 1 && <>({currentQuantity})</>}
                            </span>
                            <span className="block text-xs font-bold uppercase">
                              {totalCoins === 0
                                ? 'grátis'
                                : `por ${totalCoins} moedas`}
                            </span>
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}
